import React, { useState, useEffect, useRef } from 'react';
import { X, QrCode, RefreshCw, CheckCircle, Clock, AlertCircle, Smartphone } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

const QRPaymentModal = ({ isOpen, onClose, bookingDetails, onPaymentSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [qrData, setQrData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle | pending | succeeded | failed
  const [timeLeft, setTimeLeft] = useState(null);
  const pollingRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (isOpen && bookingDetails?.amount) {
      generateQRCode();
    }
    return () => {
      clearInterval(pollingRef?.current);
      clearInterval(timerRef?.current);
    };
  }, [isOpen, bookingDetails?.amount]);

  useEffect(() => {
    if (!isOpen) {
      clearInterval(pollingRef?.current);
      clearInterval(timerRef?.current);
      setQrData(null);
      setError('');
      setPaymentStatus('idle');
      setTimeLeft(null);
    }
  }, [isOpen]);

  const generateQRCode = async () => {
    setIsLoading(true);
    setError('');
    setQrData(null);
    setPaymentStatus('idle');
    clearInterval(pollingRef?.current);
    clearInterval(timerRef?.current);

    try {
      const { data, error: fnError } = await supabase?.functions?.invoke('create-promptpay-intent', {
        body: {
          paymentData: {
            amount: bookingDetails?.amount,
            currency: 'thb',
            description: `Phangan Taxi: ${bookingDetails?.from || ''} → ${bookingDetails?.to || ''}`,
            orderId: bookingDetails?.orderId || null,
            fromZone: bookingDetails?.fromZone || '',
            toZone: bookingDetails?.toZone || '',
            serviceType: bookingDetails?.serviceType || '',
            metadata: {
              pickupLocation: bookingDetails?.from || '',
              destination: bookingDetails?.to || '',
              serviceType: bookingDetails?.serviceType || '',
            },
          },
          customerInfo: {
            userId: bookingDetails?.userId || null,
            email: bookingDetails?.customerEmail || null,
            name: bookingDetails?.customerName || null,
          },
        },
      });

      if (fnError || data?.error) {
        throw new Error(data?.error || fnError?.message || 'Failed to generate QR code');
      }

      setQrData(data);
      setPaymentStatus('pending');

      // Start countdown timer if expiry provided
      if (data?.expiresAt) {
        const expiresAt = new Date(data.expiresAt * 1000);
        timerRef.current = setInterval(() => {
          const remaining = Math.max(0, Math.floor((expiresAt - new Date()) / 1000));
          setTimeLeft(remaining);
          if (remaining === 0) {
            clearInterval(timerRef?.current);
            setPaymentStatus('failed');
            setError('QR code expired. Please generate a new one.');
          }
        }, 1000);
      }

      // Poll for payment status
      if (data?.paymentIntentId) {
        startPolling(data?.paymentIntentId);
      }
    } catch (err) {
      setError(err?.message || 'Failed to generate QR code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const startPolling = (paymentIntentId) => {
    pollingRef.current = setInterval(async () => {
      try {
        const stripe = await stripePromise;
        if (!stripe) return;

        // Use Stripe.js to retrieve the payment intent status
        const { paymentIntent } = await stripe?.retrievePaymentIntent(qrData?.clientSecret || '');
        if (paymentIntent?.status === 'succeeded') {
          clearInterval(pollingRef?.current);
          clearInterval(timerRef?.current);
          setPaymentStatus('succeeded');

          // Confirm on backend
          try {
            await supabase?.functions?.invoke('confirm-payment', {
              body: { paymentIntentId },
            });
          } catch (e) {
            console.error('Backend confirm error:', e);
          }

          if (onPaymentSuccess) {
            onPaymentSuccess({ paymentIntentId, amount: bookingDetails?.amount });
          }
        } else if (paymentIntent?.status === 'canceled' || paymentIntent?.status === 'payment_failed') {
          clearInterval(pollingRef?.current);
          setPaymentStatus('failed');
          setError('Payment was not completed. Please try again.');
        }
      } catch (e) {
        // Silently ignore polling errors
      }
    }, 3000);
  };

  const formatTime = (seconds) => {
    if (seconds === null) return null;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s)?.padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full relative animate-fadeIn max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <QrCode className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">PromptPay QR</h2>
              <p className="text-sm text-gray-500">Scan to pay · Thai QR standard</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Amount */}
          <div className="bg-purple-50 rounded-xl p-4 text-center">
            <p className="text-sm text-purple-600 font-medium mb-1">Amount to Pay</p>
            <p className="text-3xl font-bold text-purple-700">
              ฿{bookingDetails?.amount?.toLocaleString()}
            </p>
            {bookingDetails?.from && bookingDetails?.to && (
              <p className="text-xs text-purple-500 mt-1 truncate">
                {bookingDetails?.from} → {bookingDetails?.to}
              </p>
            )}
          </div>

          {/* Loading state */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <div className="w-10 h-10 border-3 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
              <p className="text-sm text-gray-500">Generating QR code…</p>
            </div>
          )}

          {/* Error state */}
          {!isLoading && error && paymentStatus !== 'succeeded' && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
              <button
                onClick={generateQRCode}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-purple-200 text-purple-700 font-semibold hover:bg-purple-50 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
            </div>
          )}

          {/* QR Code display */}
          {!isLoading && !error && qrData && paymentStatus === 'pending' && (
            <div className="space-y-4">
              {/* Timer */}
              {timeLeft !== null && (
                <div className="flex items-center justify-center gap-2 text-sm text-amber-600 bg-amber-50 rounded-lg py-2">
                  <Clock className="w-4 h-4" />
                  <span>Expires in {formatTime(timeLeft)}</span>
                </div>
              )}

              {/* QR Image */}
              <div className="flex justify-center">
                {qrData?.qrCodeImageUrl ? (
                  <div className="border-4 border-purple-100 rounded-2xl p-2 bg-white shadow-inner">
                    <img
                      src={qrData?.qrCodeImageUrl}
                      alt="PromptPay QR Code"
                      className="w-52 h-52 object-contain"
                    />
                  </div>
                ) : (
                  <div className="border-4 border-purple-100 rounded-2xl p-6 bg-white shadow-inner flex flex-col items-center gap-2">
                    <QrCode className="w-32 h-32 text-purple-300" />
                    <p className="text-xs text-gray-400 text-center">
                      Open Stripe hosted page to scan
                    </p>
                    {qrData?.hostedInstructionsUrl && (
                      <a
                        href={qrData?.hostedInstructionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-purple-600 underline"
                      >
                        Open payment page →
                      </a>
                    )}
                  </div>
                )}
              </div>

              {/* Instructions */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                <p className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                  <Smartphone className="w-3.5 h-3.5" />
                  How to pay:
                </p>
                <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                  <li>Open your banking app (KBank, SCB, Bangkok Bank, etc.)</li>
                  <li>Select "Scan QR" or "PromptPay"</li>
                  <li>Scan the QR code above</li>
                  <li>Confirm the amount and pay</li>
                </ol>
              </div>

              {/* Polling indicator */}
              <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span>Waiting for payment confirmation…</span>
              </div>

              {/* Refresh button */}
              <button
                onClick={generateQRCode}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 text-gray-500 text-sm hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Refresh QR Code
              </button>
            </div>
          )}

          {/* Success state */}
          {paymentStatus === 'succeeded' && (
            <div className="flex flex-col items-center gap-4 py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-9 h-9 text-green-500" />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-900">Payment Received!</h3>
                <p className="text-sm text-gray-500 mt-1">
                  ฿{bookingDetails?.amount?.toLocaleString()} paid via PromptPay
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRPaymentModal;
